// src/utils/rulePlans/cloudTrailRulePlans.js
// CloudTrail-specific remediation plan helpers.

function pickTrailName(item = {}, fallback = "the affected trail") {
  const raw =
    item?.aws_context?.trail_name ||
    item?.aws_context?.trailName ||
    item?.finding_metadata?.trail_name ||
    item?.finding_metadata?.trailName ||
    item?.resource_name ||
    item?.resourceName ||
    item?.resource_id ||
    item?.resourceId ||
    fallback;

  const value = String(raw || fallback);
  if (value.includes("/")) return value.split("/").pop();
  return value;
}

function pickEventName(item = {}, fallback = "the CloudTrail event") {
  return (
    item?.aws_context?.eventName ||
    item?.aws_context?.event_name ||
    item?.finding_metadata?.eventName ||
    item?.finding_metadata?.event_name ||
    item?.event_name ||
    item?.eventName ||
    fallback
  );
}

export function isCloudTrailRule(ruleId = "") {
  const rule = String(ruleId || "").toUpperCase();

  return (
    rule.includes("CLOUDTRAIL") ||
    rule.includes("TRAIL_") ||
    rule.includes("ROOT_LOGIN") ||
    rule.includes("LOGIN_NO_MFA") ||
    rule.includes("CONSOLE_LOGIN") ||
    rule.includes("UNAUTHORIZED_API_CALL") ||
    rule.includes("STOP_LOGGING") ||
    rule.includes("DELETE_TRAIL") ||
    rule.includes("PUT_EVENT_SELECTORS")
  );
}

export function buildCloudTrailFixPlanSteps(item = {}) {
  const rule = String(item?.rule_id || item?.ruleId || "").toUpperCase();
  const trailName = pickTrailName(item);
  const eventName = pickEventName(item);

  if (rule.includes("ROOT_LOGIN")) {
    return [
      "Open AWS CloudTrail.",
      "Open Event history.",
      "Filter Event name to ConsoleLogin and Username to root.",
      "Review the event time, source IP address, user agent, MFA status, and recipient account ID.",
      "Confirm whether the root login was authorized, emergency-only access.",
      "If unauthorized, rotate root credentials, review account contacts, and investigate all activity around the event time.",
      "Confirm root MFA is enabled and break-glass access is documented.",
      "Document the investigation result and any approved exception in OASYS.",
      "Run Full Cloud Assessment to refresh the root-login evidence and related IAM posture.",
    ];
  }

  if (rule.includes("LOGIN_NO_MFA") || rule.includes("CONSOLE_LOGIN_NO_MFA")) {
    return [
      "Open AWS CloudTrail.",
      "Open Event history.",
      "Filter for ConsoleLogin events around the finding timestamp.",
      "Identify the IAM user or federated principal that logged in without MFA.",
      "Confirm whether the login was approved and whether MFA should have been enforced.",
      "Enable or enforce MFA for the affected identity or identity provider.",
      "Review related sign-in events for suspicious activity.",
      "Document the investigation and any exception in OASYS.",
      "Run Full Cloud Assessment to verify login/MFA posture is refreshed.",
    ];
  }

  if (rule.includes("STOP_LOGGING") || rule.includes("CLOUDTRAIL_STOP_LOGGING")) {
    return [
      "Open AWS CloudTrail.",
      "Open Event history and filter Event name to StopLogging.",
      "Identify the trail, actor, source IP, and event time tied to the stop-logging event.",
      `Open Trails and select ${trailName}.`,
      "Confirm logging is currently enabled for the trail.",
      "If logging was stopped without approval, re-enable logging immediately and investigate the actor activity around the event time.",
      "Document the approval, incident review, or corrective action in OASYS.",
      "Run Full Cloud Assessment to confirm CloudTrail logging evidence is current.",
    ];
  }

  if (rule.includes("DELETE_TRAIL")) {
    return [
      "Open AWS CloudTrail.",
      "Open Event history and filter Event name to DeleteTrail.",
      "Identify the deleted trail, actor, source IP, and event time.",
      "Confirm whether the deletion was approved and whether replacement logging exists.",
      "Recreate the trail or restore equivalent organization-level logging if coverage was lost.",
      "Confirm logs are delivered to the approved S3 bucket and optional CloudWatch Logs destination.",
      "Document the change record or incident review in OASYS.",
      "Run Full Cloud Assessment to verify trail coverage is restored.",
    ];
  }

  if (rule.includes("MULTI_REGION")) {
    return [
      "Open AWS CloudTrail.",
      "Go to Trails.",
      `Open ${trailName}.`,
      "Choose Edit for the trail configuration.",
      "Enable Apply trail to all regions unless an approved exception exists.",
      "Confirm management events are still enabled and the delivery bucket is correct.",
      "Save changes and document any exception in OASYS.",
      "Run Full Cloud Assessment to verify multi-region trail coverage is detected.",
    ];
  }

  if (rule.includes("LOG_FILE_VALIDATION") || rule.includes("FILE_VALIDATION")) {
    return [
      "Open AWS CloudTrail.",
      "Go to Trails.",
      `Open ${trailName}.`,
      "Choose Edit for the trail configuration.",
      "Enable Log file validation.",
      "Save changes and confirm validation is enabled on the trail.",
      "Document any approved exception in OASYS.",
      "Run Full Cloud Assessment to verify log file validation is detected.",
    ];
  }

  if (rule.includes("KMS") || rule.includes("ENCRYPT")) {
    return [
      "Open AWS CloudTrail.",
      "Go to Trails.",
      `Open ${trailName}.`,
      "Choose Edit for the trail configuration.",
      "Enable SSE-KMS encryption using the approved KMS key.",
      "Confirm the KMS key policy permits CloudTrail to encrypt log files and approved reviewers to decrypt them.",
      "Save changes and document the KMS key used.",
      "Run Full Cloud Assessment to verify CloudTrail encryption evidence is refreshed.",
    ];
  }

  if (rule.includes("CLOUDWATCH") || rule.includes("LOG_GROUP") || rule.includes("LOGGING")) {
    return [
      "Open AWS CloudTrail.",
      "Go to Trails.",
      `Open ${trailName}.`,
      "Review CloudWatch Logs integration and the target log group.",
      "Enable or repair CloudWatch Logs delivery if required by the governing control.",
      "Confirm the CloudTrail service role has permission to write to the log group.",
      "Save changes and confirm recent events are being delivered.",
      "Run Full Cloud Assessment to verify log delivery evidence is refreshed.",
    ];
  }

  if (rule.includes("EVENT_SELECTOR") || rule.includes("DATA_EVENTS") || rule.includes("MANAGEMENT_EVENTS")) {
    return [
      "Open AWS CloudTrail.",
      "Go to Trails.",
      `Open ${trailName}.`,
      "Open Event selectors.",
      "Confirm management events are enabled and set to the required read/write scope.",
      "Enable required data events for covered resources such as S3 buckets or Lambda functions, if required by the control.",
      "Save selector changes and document any approved exclusion.",
      "Run Full Cloud Assessment to verify event selector coverage is refreshed.",
    ];
  }

  if (rule.includes("UNAUTHORIZED_API_CALL")) {
    return [
      "Open AWS CloudTrail.",
      "Open Event history.",
      "Filter for AccessDenied, UnauthorizedOperation, or the event name referenced by the finding.",
      "Identify the actor, source IP, target service, and denied action.",
      "Confirm whether the denied activity was expected testing, misconfigured automation, or suspicious behavior.",
      "Correct the workload permission boundary if the access is approved and required.",
      "Remove or restrict access if the denied attempt was inappropriate or suspicious.",
      "Document the investigation result in OASYS.",
      "Run Full Cloud Assessment to refresh the CloudTrail event evidence.",
    ];
  }

  return [
    "Open AWS CloudTrail.",
    "Open Trails or Event history depending on the finding evidence.",
    `Review ${eventName} evidence and the affected trail or actor.`,
    "Confirm whether the event or trail configuration is approved and matches the governing logging control.",
    "Apply the required logging, encryption, event-selector, or investigation remediation.",
    "Document approval, exception, or incident response notes in OASYS.",
    "Run Full Cloud Assessment to verify CloudTrail evidence and control status are refreshed.",
  ];
}

export function buildCloudTrailValidationText(item = {}) {
  const rule = String(item?.rule_id || item?.ruleId || "").toUpperCase();

  if (rule.includes("ROOT_LOGIN") || rule.includes("LOGIN_NO_MFA") || rule.includes("CONSOLE_LOGIN")) {
    return "OASYS will mark this improved after the next sync confirms the CloudTrail sign-in event was investigated and related IAM access posture is corrected or formally excepted.";
  }

  if (rule.includes("STOP_LOGGING") || rule.includes("DELETE_TRAIL")) {
    return "OASYS will mark this improved after the next sync confirms CloudTrail logging coverage is restored and the event has been investigated.";
  }

  if (rule.includes("MULTI_REGION")) {
    return "OASYS will mark this improved after the next sync detects the affected trail is configured for all-region coverage or has an approved exception.";
  }

  if (rule.includes("LOG_FILE_VALIDATION") || rule.includes("FILE_VALIDATION")) {
    return "OASYS will mark this improved after the next sync detects log file validation is enabled for the affected trail.";
  }

  if (rule.includes("KMS") || rule.includes("ENCRYPT")) {
    return "OASYS will mark this improved after the next sync detects CloudTrail log encryption is configured with an approved KMS key.";
  }

  return "OASYS will mark this improved after the next sync refreshes CloudTrail trail configuration or event evidence and recalculates the related control.";
}
